import { createSelector, Dispatch } from '@reduxjs/toolkit';
import { fetchAvailability, setOfferNotification } from 'component/page/detail/store/availabilityIndex';
import {
    selectAvailabilityIsLoading,
    selectAvailabilityIsSearchCriteriaUpdated,
    selectAvailabilitySearchCriteriaStatus,
    selectAvailabilitySelectedOfferDetail,
    selectFinalOfferOption,
    selectIsCartEligibleSelected,
    selectIsOfferDateChipSelected,
    selectOfferLevelSelectedDate,
    selectOfferOptionTokenFromOfferSelected,
    selectOfferSelected,
    selectSelectedBookingRequirement,
    selectTotalMaxPax,
    selectTotalPax,
} from 'component/page/detail/store/availabilitySelectorIndex';
import { AvailabilitySearchCriteriaStatus, getMinPassengerError } from 'component/page/detail/PackageOptions/ExpandedOffer/OfferCardPaxPanel/PaxCounter/paxUtils';
import { selectBrowserInfoFromBootstrap, selectConfigFromBootstrap } from 'store/slice/bootstrap';
import { BootstrapConfig, CmsValue } from 'core/type/BootstrapConfig.types';
import { setWindowLocation } from 'core/window/windowService';
import { StringFormatter } from '@agoda/react-handyman';
import { EnrichedBookingRequirement, SelectedOfferState } from 'component/page/detail/store/availabilityIndex.types';
import { createBookingFromUrl } from 'component/page/detail/detailUtils';
import { navigateToBF1, selectPageTypeId } from 'store/slice/navigation';
import { selectActivityTokenFromDetailState, selectIsCartMigrationEnabled, selectShouldSendCartMigrationFallbackMeasurement } from 'component/page/detail/store/detail.selectors';
import { buildMeasurement } from 'core/service/measurements/measurementUtils';
import { MeasurementName } from 'core/constant/MeasurementName';
import { sendMeasurement } from 'core/messaging/measurement/measurement';
import { EnrichedOfferOption } from 'gql/autogen/EnrichedOfferOption';
import { ActivityOfferToken } from 'core/type/Details.types';
import { isB, isOn } from 'core/window/bootstrapConfig';
import { FeatureName } from 'core/constant/FeatureName';
import { ExperimentId } from 'core/type/ExperimentId';
import { selectNumberOfAdults } from 'component/page/detail/CartCrossSellPanel/criteriaExtractor.selectors';
import { ScreenSize, selectScreenSize } from 'store/slice/screen';
import { BookButtonState } from 'component/page/detail/PackageOptions/ExpandedOffer/OfferCardBookButtonSection/BookButtonContainer';
import { selectBookButtonState } from 'component/page/detail/store/InteractionState.selectors';

export type OfferCardFooterProps = OwnProps & StateProps & DispatchProps;

export enum OfferCardFooterType {
    Boxed = 'boxed',
    Sticky = 'sticky',
    SummaryCard = 'summaryCard',
}

interface OwnProps {
    footerType: OfferCardFooterType;
    offerIndex: number;
}

interface StateProps {
    config: BootstrapConfig;
    isLoading: boolean;
    isButtonDisabled: boolean;
    isSearchCriteriaUpdated: boolean;
    hasOnlyDisabledOptions: boolean;
    bookingFormUrl: string;
    searchCriteriaStatus: AvailabilitySearchCriteriaStatus;
    selectedOffer: SelectedOfferState;
    selectedDate: Date;
    offerTitle: string;
    isCartEligible: boolean;
    isCartMigrationEnabled: boolean;
    isWebView: boolean;
    isDesktop: boolean;
    isDetailRevampEnabled: boolean;
    activityTokenFromState: string;
    offerOptionToken: string;
    pageTypeId: number;
    shouldSendCartMigrationFallbackMeasurement: boolean;
    isOfferDateChipSelected: boolean;
    isAllOfferSpecSelected: boolean;
    finalOfferOption?: EnrichedOfferOption;
    showInclusiveTax: boolean;
    expandFirstOfferCardOnDetailsLanding: boolean;
    cms: CmsValue;
    screenSize: ScreenSize;
    bookButtonState: BookButtonState;
    isDetailPageRefactor: boolean;
    isDetailPageForSimCard: boolean;
}

interface DispatchProps {
    updateOffer: (offerToken: ActivityOfferToken) => void;
    navigateToBookingForm: (
        bookingFormUrl: string,
        isWebView: boolean,
        isCartMigrationEnabled: boolean,
        pageTypeId: number,
        shouldSendCartMigrationFallbackMeasurement: boolean,
    ) => void;
    setOfferNotification: (offerNotification: Record<string, boolean>) => void;
}

export const AvailabilitySearchCriteriaStatusErrorMapper: {
    [key in AvailabilitySearchCriteriaStatus]: (
        cms: CmsValue,
        requirement: Required<EnrichedBookingRequirement>,
        isAct4603?: boolean,
        isUpdateAvailabilitySlotAlert?: boolean,
    ) => string;
} = {
    [AvailabilitySearchCriteriaStatus.NoError]: (cms: CmsValue, requirement: Required<EnrichedBookingRequirement>) => '',
    [AvailabilitySearchCriteriaStatus.MaxPaxExceed]: (cms: CmsValue, requirement: Required<EnrichedBookingRequirement>, _undefined, isUpdateAvailabilitySlotAlert?: boolean) =>
        StringFormatter.format(isUpdateAvailabilitySlotAlert ? cms[409794] : cms[225581], requirement.maxPax),
    [AvailabilitySearchCriteriaStatus.MinPaxRequired]: (cms: CmsValue, requirement: Required<EnrichedBookingRequirement>, isAct4603?: boolean) =>
        getMinPassengerError(cms, requirement, isAct4603 ?? false),
    [AvailabilitySearchCriteriaStatus.MaxAvailableExceed]: (cms: CmsValue, _, _undefined, isUpdateAvailabilitySlotAlert?: boolean) =>
        isUpdateAvailabilitySlotAlert ? cms[409795] : cms[344619],
};

export const mapStateToProps = createSelector(
    selectConfigFromBootstrap,
    selectAvailabilityIsSearchCriteriaUpdated,
    selectAvailabilitySearchCriteriaStatus,
    selectAvailabilityIsLoading,
    selectOfferSelected,
    selectOfferLevelSelectedDate,
    selectAvailabilitySelectedOfferDetail,
    selectIsCartEligibleSelected,
    selectIsCartMigrationEnabled,
    selectBrowserInfoFromBootstrap,
    selectActivityTokenFromDetailState,
    selectOfferOptionTokenFromOfferSelected,
    selectPageTypeId,
    selectShouldSendCartMigrationFallbackMeasurement,
    selectIsOfferDateChipSelected,
    selectFinalOfferOption,
    selectTotalPax,
    selectTotalMaxPax,
    selectSelectedBookingRequirement,
    selectNumberOfAdults,
    selectScreenSize,
    selectBookButtonState,
    (
        config,
        isSearchCriteriaUpdated,
        searchCriteriaStatus,
        isLoading,
        selectedOffer,
        selectedDate,
        offerDetail,
        isCartEligible,
        isCartMigrationEnabled,
        browserInfo,
        activityTokenFromState,
        offerOptionToken,
        pageTypeId,
        shouldSendCartMigrationFallbackMeasurement,
        isOfferDateChipSelected,
        finalOfferOption,
        totalPax,
        totalMaxPax,
        selectedBookingRequirement,
        numberOfAdults,
        screenSize,
        bookButtonState,
    ): StateProps => {
        const offer = selectedOffer.offer;
        const bookingFormUrl = offer ? createBookingFromUrl(isCartMigrationEnabled, finalOfferOption) : '';
        const hasOnlyDisabledOptions = (offer?.enrichedOfferOption || []).filter((option) => option.isAvailable).length === 0;
        const isMinPaxRequiredErrorMessage = [AvailabilitySearchCriteriaStatus.MinPaxRequired].includes(searchCriteriaStatus);
        const bookingRequirementMinPax = selectedBookingRequirement.minPax || 1;
        const isRequiredMinAdultsMet = !selectedBookingRequirement.adultRequired || numberOfAdults >= bookingRequirementMinPax;
        const isTotalExceedMaxPax = totalPax > totalMaxPax;
        const isCheckAvailableQuantity = isB(ExperimentId.ACT_4598, config.experiments);
        // Need to check isRequiredMinAdultsMet separately because errorMessage type will be overridden when max pax reached since we show another message
        const isButtonDisabled = isCheckAvailableQuantity
            ? !isLoading && (!isRequiredMinAdultsMet || isTotalExceedMaxPax || isMinPaxRequiredErrorMessage || (!isSearchCriteriaUpdated && hasOnlyDisabledOptions))
            : !isLoading && (isMinPaxRequiredErrorMessage || (!isSearchCriteriaUpdated && hasOnlyDisabledOptions));
        const offerTitle = offerDetail?.title || '';
        const isWebView = browserInfo.isWebView;
        const isDesktop = browserInfo.isDesktop;
        const isDetailRevampEnabled = isOn(FeatureName.DetailPageRevamp);
        const isAllOfferSpecSelected = !!finalOfferOption;
        const showInclusiveTax = config.behavior.common.showInclusiveTax;
        const expandFirstOfferCardOnDetailsLanding = isOn(FeatureName.ExpandFirstOfferOnDetailsLanding);
        const cms = config.cms;
        const isDetailPageRefactor = isB(ExperimentId.ACT_4944, config.experiments);
        const isDetailPageForSimCard = isOn(FeatureName.DetailPageForSimCard);
        return {
            cms,
            config,
            isLoading,
            isSearchCriteriaUpdated,
            searchCriteriaStatus,
            selectedOffer,
            selectedDate,
            isButtonDisabled,
            hasOnlyDisabledOptions,
            bookingFormUrl,
            offerTitle,
            isCartEligible,
            isCartMigrationEnabled,
            isWebView,
            isDesktop,
            isDetailRevampEnabled,
            activityTokenFromState,
            offerOptionToken,
            pageTypeId,
            shouldSendCartMigrationFallbackMeasurement,
            isOfferDateChipSelected,
            isAllOfferSpecSelected,
            finalOfferOption,
            showInclusiveTax,
            expandFirstOfferCardOnDetailsLanding,
            screenSize,
            bookButtonState,
            isDetailPageRefactor,
            isDetailPageForSimCard,
        };
    },
);

export const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
    updateOffer: (offerToken: ActivityOfferToken) => {
        fetchAvailability(offerToken.activityToken, { excludePassengers: false, enableOfferFilter: offerToken.enableOfferFilter });
    },
    navigateToBookingForm: (
        bookingFormUrl: string,
        isWebView: boolean,
        isCartMigrationEnabled: boolean,
        pageTypeId: number,
        shouldSendCartMigrationFallbackMeasurement: boolean,
    ) => {
        if (shouldSendCartMigrationFallbackMeasurement) {
            const measurement = buildMeasurement('navigate_bf1', MeasurementName.NavigateBookingFormFallback, pageTypeId);
            sendMeasurement(measurement, pageTypeId);
        }
        isCartMigrationEnabled ? navigateToBF1(bookingFormUrl, isWebView) : setWindowLocation(bookingFormUrl);
    },
    setOfferNotification: (offerNotification: Record<string, boolean>) => {
        dispatch(setOfferNotification(offerNotification));
    },
});
